import { RawDraftContentState } from 'draft-js';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch } from '../../../hooks/react-redux';
import { Mail } from '../../shared/icons';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { ReportEmailContent } from '../../shared/report/ReportEmailContent';
import { SectionWrapper } from '../../shared/report/SectionWrapper';
import { Text } from '../../shared/text/Text';
import styles from './PortfolioAnalytics.module.scss';
import { emailPortfolioAnalyticsStarted, PortfolioAnalyticsReportEmail, toggleEmailModal, updateEmailValue } from './store';

interface PortfolioAnalyticsEmailModalProps {
    reportEmail: PortfolioAnalyticsReportEmail;
}

export const PortfolioAnalyticsEmailModal: React.FC<PortfolioAnalyticsEmailModalProps> = ({ reportEmail }) => {
    const { filename, userIds, emailBody } = reportEmail;
    const dispatch = useAppDispatch();
    const closeModal = useCallback(() => dispatch(toggleEmailModal(null)), [dispatch]);
    const updateFilename = useCallback((value: string) => dispatch(updateEmailValue('filename', value)), [dispatch]);
    const updateSelectedUserIds = useCallback((value: number[]) => dispatch(updateEmailValue('userIds', value)), [dispatch]);
    const updateEmailBody = useCallback((value: RawDraftContentState | null) => dispatch(updateEmailValue('emailBody', value)), [dispatch]);

    const confirm = useCallback(() => dispatch(emailPortfolioAnalyticsStarted()), [dispatch]);
    const confirmDisabled = useMemo(() => filename.length === 0 || userIds.length === 0, [filename, userIds]);
    const confirmDisabledTooltip = useMemo(() => {
        let disabledTooltip = [];
        if (userIds.length === 0) {
            disabledTooltip.push('You must select at least one user if you would like to send this report as an email');
        }
        if (filename === '') {
            disabledTooltip.push('You must provide a file name for the attachment of this email');
        }
        return disabledTooltip;
    }, [userIds, filename]);
    const testId = 'portfolio-analytics-report-email';

    return (
        <ConfirmationModal
            isOpen
            closeModal={closeModal}
            confirm={confirm}
            confirmDisabled={confirmDisabled}
            confirmDisabledTooltip={confirmDisabledTooltip}
            closeOnOverlayClick={false}
            spaceBetweenButtons
            confirmLabel='Send'
        >
            <div className={styles.reportEmailModal}>
                <ModalHeader
                    label='Email Portfolio Analytics Report'
                    testId={`${testId}-modal`}
                    icon={Mail}
                />
                <Text
                    onChange={e => updateFilename(e.currentTarget.value)}
                    value={filename}
                    testId={`${testId}-file-name`}
                    placeholder='File Name...'
                    label='File Name'
                    width='calc(100% - 12px)'
                />
                <SectionWrapper id={`${testId}-content`} label='Email Details' description='Select the users you would like to email this report to and add an optional personal message'>
                    <ReportEmailContent emailBody={emailBody} selectedUserIds={userIds} testId={testId} updateEmailBody={updateEmailBody} updateSelectedUserIds={updateSelectedUserIds} />
                </SectionWrapper>
            </div>
        </ ConfirmationModal>
    );
};
